import { useState, useEffect, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { safetyChecksApi, checklistApi, pdvApi, inspectorsApi, pdvChecklistsApi, resolveNonConformity } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ClipboardCheck,
  Plus,
  Search,
  Building,
  CheckCircle2,
  XCircle,
  MinusCircle,
  ChevronDown,
  ChevronUp,
  Award,
  Printer,
  Settings,
  User,
  MapPin,
  RotateCcw,
  Check,
  X,
  Mail,
  Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';

// Context for selected PDV
const SelectedPDVContext = createContext(null);

const useSelectedPDV = () => useContext(SelectedPDVContext);

const PDVSelector = ({ pdvs, selectedPdv, onSelect }) => (
  <Card className="mb-6 border-2 border-primary/20">
    <CardContent className="p-4">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
          <Building className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider">Unità Locale Selezionata</Label>
          <Select value={selectedPdv?.id || ''} onValueChange={(id) => onSelect(pdvs.find(p => p.id === id))}>
            <SelectTrigger className="mt-1 text-lg font-semibold" data-testid="pdv-selector">
              <SelectValue placeholder="Seleziona un'Unità Locale" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {pdvs.map((pdv) => (
                <SelectItem key={pdv.id} value={pdv.id}>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span>{pdv.name}</span>
                    <span className="text-muted-foreground">- {pdv.city}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {selectedPdv && (
          <Button variant="outline" size="sm" onClick={() => onSelect(null)}>
            <X className="w-4 h-4 mr-1" /> Cambia
          </Button>
        )}
      </div>
    </CardContent>
  </Card>
);

const ChecklistConfigDialog = ({ pdv, templates, onUpdate }) => {
  const [pdvTemplates, setPdvTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (pdv && templates?.length > 0) {
      loadPdvChecklists();
    }
  }, [pdv, templates]);

  const loadPdvChecklists = async () => {
    try {
      const response = await pdvChecklistsApi.getForPdv(pdv.id);
      // Handle both response formats:
      // Format 1 (server): Array of templates with is_active_for_pdv
      // Format 2 (local): {pdv_id, active_template_ids}
      if (Array.isArray(response.data)) {
        // Server format: direct array of templates
        setPdvTemplates(response.data);
      } else {
        // Local format: combine templates with active_template_ids
        const activeIds = response.data?.active_template_ids || [];
        const combinedTemplates = templates.map(t => ({
          ...t,
          is_active_for_pdv: activeIds.includes(t.id)
        }));
        setPdvTemplates(combinedTemplates);
      }
    } catch (error) {
      console.error('Error loading PDV checklists:', error);
      setPdvTemplates(templates.map(t => ({ ...t, is_active_for_pdv: false })));
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (template, isActive) => {
    try {
      if (isActive) {
        await pdvChecklistsApi.activate(pdv.id, template.id);
      } else {
        await pdvChecklistsApi.deactivate(pdv.id, template.id);
      }
      loadPdvChecklists();
      onUpdate();
      toast.success(isActive ? 'Checklist attivata' : 'Checklist disattivata');
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  if (loading) return <div className="p-4 text-center">Caricamento...</div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Attiva le checklist che vuoi utilizzare per <strong>{pdv.name}</strong>
      </p>
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {pdvTemplates.map((template) => (
          <div key={template.id} className="flex items-center justify-between p-3 border border-border rounded-sm">
            <div className="flex-1">
              <p className="font-medium">{template.name}</p>
              <p className="text-xs text-muted-foreground">{template.items?.length || 0} voci • {template.category}</p>
            </div>
            <Switch
              checked={template.is_active_for_pdv}
              onCheckedChange={(checked) => handleToggle(template, checked)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const InspectorSelector = ({ inspectors, value, onChange, onAddNew }) => {
  const [showAddNew, setShowAddNew] = useState(false);
  const [newName, setNewName] = useState('');

  const handleAddNew = async () => {
    if (!newName.trim()) return;
    try {
      await inspectorsApi.create({ name: newName.trim() });
      toast.success('Ispettore aggiunto');
      setNewName('');
      setShowAddNew(false);
      onAddNew();
    } catch (error) {
      toast.error('Errore nell\'aggiunta');
    }
  };

  return (
    <div className="space-y-2">
      <Label>Ispettore *</Label>
      {!showAddNew ? (
        <div className="flex gap-2">
          <Select value={value} onValueChange={onChange}>
            <SelectTrigger className="flex-1" data-testid="inspector-select">
              <User className="w-4 h-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder="Seleziona ispettore" />
            </SelectTrigger>
            <SelectContent>
              {inspectors.map((insp) => (
                <SelectItem key={insp.id} value={insp.name}>
                  {insp.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button type="button" variant="outline" size="icon" onClick={() => setShowAddNew(true)}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Nome nuovo ispettore"
            autoFocus
          />
          <Button type="button" size="icon" onClick={handleAddNew}>
            <Check className="w-4 h-4" />
          </Button>
          <Button type="button" variant="outline" size="icon" onClick={() => setShowAddNew(false)}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

const ChecklistForm = ({ pdv, templates, inspectors, onSubmit, onCancel, onInspectorAdded }) => {
  const [step, setStep] = useState(1); // 1=inspector selection, 2+=checklists
  
  // Get only active templates for this PDV - these are already configured!
  const activeTemplates = templates.filter(t => t.is_active_for_pdv);
  
  const [formData, setFormData] = useState({
    pdv_id: pdv?.id || '',
    inspector_name: '',
    checklists: [], // Will be auto-populated from activeTemplates
    general_notes: ''
  });
  const [currentChecklistIndex, setCurrentChecklistIndex] = useState(0);

  // Start the checklist flow - auto-load ALL active checklists
  const handleStartChecklists = () => {
    if (!formData.inspector_name) {
      toast.error('Seleziona un ispettore');
      return;
    }
    
    // Auto-load ALL active checklists - no manual selection needed!
    const checklists = activeTemplates.map(template => ({
      template_id: template.id,
      template_name: template.name,
      items: template.items.map(item => ({ item, status: 'conforme', notes: '' }))
    }));
    
    setFormData({ ...formData, checklists });
    setCurrentChecklistIndex(0);
    setStep(2);
  };

  const handleItemStatusChange = (itemIndex, status) => {
    const newChecklists = [...formData.checklists];
    newChecklists[currentChecklistIndex].items[itemIndex].status = status;
    setFormData({ ...formData, checklists: newChecklists });
  };

  const handleItemNotesChange = (itemIndex, notes) => {
    const newChecklists = [...formData.checklists];
    newChecklists[currentChecklistIndex].items[itemIndex].notes = notes;
    setFormData({ ...formData, checklists: newChecklists });
  };

  const handleNextChecklist = () => {
    if (currentChecklistIndex < formData.checklists.length - 1) {
      setCurrentChecklistIndex(currentChecklistIndex + 1);
    }
  };

  const handlePrevChecklist = () => {
    if (currentChecklistIndex > 0) {
      setCurrentChecklistIndex(currentChecklistIndex - 1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.checklists.length === 0 || !formData.inspector_name) {
      toast.error('Seleziona un ispettore');
      return;
    }
    // Submit each checklist as a separate safety check
    formData.checklists.forEach(checklist => {
      onSubmit({
        pdv_id: formData.pdv_id,
        template_id: checklist.template_id,
        inspector_name: formData.inspector_name,
        items: checklist.items,
        general_notes: formData.general_notes
      });
    });
  };

  const currentChecklist = formData.checklists[currentChecklistIndex];
  const currentItems = currentChecklist?.items || [];
  const conformeCount = currentItems.filter(i => i.status === 'conforme').length;
  const totalApplicable = currentItems.filter(i => i.status !== 'na').length;
  const score = totalApplicable > 0 ? Math.round((conformeCount / totalApplicable) * 100) : 0;

  const isLastChecklist = currentChecklistIndex === formData.checklists.length - 1;
  const isFirstChecklist = currentChecklistIndex === 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {step === 1 && (
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
          <div className="p-3 bg-secondary/50 rounded-sm">
            <p className="text-sm font-medium flex items-center gap-2">
              <Building className="w-4 h-4" />
              {pdv?.name}
            </p>
            <p className="text-xs text-muted-foreground">{pdv?.address}, {pdv?.city}</p>
          </div>

          {/* Show active checklists summary - no selection needed! */}
          {activeTemplates.length > 0 ? (
            <div className="space-y-2">
              <Label>Checklist da compilare ({activeTemplates.length})</Label>
              <div className="p-3 bg-primary/5 border border-primary/20 rounded-sm">
                <div className="flex flex-wrap gap-2">
                  {activeTemplates.map((tpl) => (
                    <Badge key={tpl.id} variant="secondary" className="text-xs">
                      {tpl.name} ({tpl.items?.length} voci)
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Le checklist sono state configurate in "Configura Checklist"
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 border border-dashed border-destructive rounded-sm text-center">
              <p className="text-sm text-destructive">Nessuna checklist attiva per questa UL</p>
              <p className="text-xs text-muted-foreground mt-1">Vai su "Configura Checklist" per abilitare le checklist</p>
            </div>
          )}

          <InspectorSelector
            inspectors={inspectors}
            value={formData.inspector_name}
            onChange={(name) => setFormData({ ...formData, inspector_name: name })}
            onAddNew={onInspectorAdded}
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onCancel}>Annulla</Button>
            <Button
              type="button"
              onClick={handleStartChecklists}
              disabled={activeTemplates.length === 0 || !formData.inspector_name}
              data-testid="check-next-btn"
            >
              Avanti - Inizia Controllo ({activeTemplates.length} checklist)
            </Button>
          </div>
        </motion.div>
      )}

      {step === 2 && currentChecklist && (
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
          {/* Progress indicator */}
          <div className="flex items-center justify-between mb-2 p-3 bg-secondary/30 rounded-sm">
            <div className="flex items-center gap-2">
              <Badge variant="outline">
                Checklist {currentChecklistIndex + 1} di {formData.checklists.length}
              </Badge>
              <span className="font-heading font-bold">{currentChecklist.template_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Punteggio:</span>
              <span className={`text-lg font-bold ${score >= 80 ? 'text-emerald-600' : score >= 50 ? 'text-amber-600' : 'text-rose-600'}`}>
                {score}%
              </span>
            </div>
          </div>

          {/* Checklist navigation dots */}
          {formData.checklists.length > 1 && (
            <div className="flex items-center justify-center gap-2 mb-4">
              {formData.checklists.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setCurrentChecklistIndex(idx)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    idx === currentChecklistIndex 
                      ? 'bg-primary' 
                      : idx < currentChecklistIndex 
                        ? 'bg-emerald-500' 
                        : 'bg-gray-300'
                  }`}
                  title={formData.checklists[idx].template_name}
                />
              ))}
            </div>
          )}

          <div className="space-y-3 max-h-[350px] overflow-y-auto pr-2">
            {currentItems.map((item, index) => (
              <Card key={index} className="border border-border">
                <CardContent className="p-4">
                  <p className="font-medium mb-3">{item.item}</p>
                  <RadioGroup
                    value={item.status}
                    onValueChange={(value) => handleItemStatusChange(index, value)}
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="conforme" id={`conforme-${currentChecklistIndex}-${index}`} />
                      <Label htmlFor={`conforme-${currentChecklistIndex}-${index}`} className="flex items-center gap-1 cursor-pointer text-emerald-600">
                        <CheckCircle2 className="w-4 h-4" /> Conforme
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="non_conforme" id={`non_conforme-${currentChecklistIndex}-${index}`} />
                      <Label htmlFor={`non_conforme-${currentChecklistIndex}-${index}`} className="flex items-center gap-1 cursor-pointer text-rose-600">
                        <XCircle className="w-4 h-4" /> Non Conforme
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="na" id={`na-${currentChecklistIndex}-${index}`} />
                      <Label htmlFor={`na-${currentChecklistIndex}-${index}`} className="flex items-center gap-1 cursor-pointer text-muted-foreground">
                        <MinusCircle className="w-4 h-4" /> N/A
                      </Label>
                    </div>
                  </RadioGroup>
                  {item.status === 'non_conforme' && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-3">
                      <Input
                        placeholder="Note sulla non conformità..."
                        value={item.notes}
                        onChange={(e) => handleItemNotesChange(index, e.target.value)}
                        className="text-sm"
                      />
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* General notes only on last checklist */}
          {isLastChecklist && (
            <div className="space-y-2">
              <Label>Note Generali</Label>
              <textarea
                value={formData.general_notes}
                onChange={(e) => setFormData({ ...formData, general_notes: e.target.value })}
                placeholder="Osservazioni generali sul controllo..."
                className="w-full min-h-[80px] px-3 py-2 rounded-sm border border-input bg-background text-sm resize-none"
              />
            </div>
          )}

          <div className="flex justify-between gap-3 pt-4">
            <div className="flex gap-2">
              {isFirstChecklist ? (
                <Button type="button" variant="outline" onClick={() => setStep(1)}>
                  Indietro
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={handlePrevChecklist}>
                  ← Checklist Precedente
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {!isLastChecklist ? (
                <Button type="button" onClick={handleNextChecklist} data-testid="check-next-checklist-btn">
                  Checklist Successiva →
                </Button>
              ) : (
                <Button type="submit" data-testid="check-submit-btn">
                  <ClipboardCheck className="w-4 h-4 mr-2" /> Completa Controllo
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </form>
  );
};

const ResolveDialog = ({ check, itemIndex, onResolve, onClose, inspectors }) => {
  const [notes, setNotes] = useState('');
  const [resolvedBy, setResolvedBy] = useState('');
  const item = check?.items?.[itemIndex];

  const handleSubmit = async () => {
    if (!notes || !resolvedBy) {
      toast.error('Compila tutti i campi');
      return;
    }
    try {
      await resolveNonConformity(check.id, {
        item_index: itemIndex,
        resolution_notes: notes,
        resolved_by: resolvedBy
      });
      toast.success('Non conformità risolta');
      onResolve();
      onClose();
    } catch (error) {
      toast.error('Errore nella risoluzione');
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-3 bg-rose-50 border border-rose-200 rounded-sm">
        <p className="text-sm font-medium text-rose-800">{item?.item}</p>
        {item?.notes && <p className="text-xs text-rose-600 mt-1">Note: {item.notes}</p>}
      </div>

      <div className="space-y-2">
        <Label>Azioni correttive eseguite *</Label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Descrivi le azioni correttive..."
          className="w-full min-h-[100px] px-3 py-2 rounded-sm border border-input bg-background text-sm resize-none"
        />
      </div>

      <InspectorSelector
        inspectors={inspectors}
        value={resolvedBy}
        onChange={setResolvedBy}
        onAddNew={() => {}}
      />

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={onClose}>Annulla</Button>
        <Button onClick={handleSubmit} className="bg-emerald-600 hover:bg-emerald-700">
          <Check className="w-4 h-4 mr-2" /> Segna come Risolto
        </Button>
      </div>
    </div>
  );
};

const CheckCard = ({ check, pdvs, inspectors, expanded, onToggle, onRefresh }) => {
  const pdv = pdvs.find(p => p.id === check.pdv_id);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);

  const handlePrintReport = async () => {
    const reportUrl = `/api/safety-checks/${check.id}/report`;
    const token = sessionStorage.getItem('token');
    try {
      const response = await fetch(reportUrl, { headers: { 'Authorization': `Bearer ${token}` } });
      const html = await response.text();
      const printWindow = window.open('', '_blank');
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 500);
    } catch (error) {
      toast.error('Errore nel caricamento del report');
    }
  };

  const handleResolveClick = (index) => {
    setSelectedItemIndex(index);
    setResolveDialogOpen(true);
  };

  return (
    <Card className="hover-card" data-testid={`check-card-${check.id}`}>
      <CardHeader className="pb-2 cursor-pointer" onClick={onToggle}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <CardTitle className="text-lg font-heading">{check.template_name}</CardTitle>
              <Badge variant={check.status === 'completed' ? 'default' : 'destructive'}>
                {check.status === 'completed' ? 'Completato' : 'Azione Richiesta'}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              {pdv?.name || 'N/D'} • {new Date(check.inspection_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })} • {check.inspector_name}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className={`text-2xl font-bold ${
                check.conformity_score >= 80 ? 'text-emerald-600' :
                check.conformity_score >= 50 ? 'text-amber-600' : 'text-rose-600'
              }`}>
                {check.conformity_score}%
              </p>
              <Progress value={check.conformity_score} className="w-24 h-2" />
            </div>
            <Button variant="ghost" size="icon">
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </CardHeader>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
            <CardContent className="pt-4 border-t border-border">
              <div className="space-y-2">
                {check.items.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 py-2 border-b border-border last:border-0">
                    <div className="mt-0.5">
                      {item.status === 'conforme' && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                      {item.status === 'risolto' && <CheckCircle2 className="w-5 h-5 text-blue-600" />}
                      {item.status === 'non_conforme' && <XCircle className="w-5 h-5 text-rose-600" />}
                      {item.status === 'na' && <MinusCircle className="w-5 h-5 text-muted-foreground" />}
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm ${item.status === 'non_conforme' ? 'text-rose-600 font-medium' : ''}`}>
                        {item.item}
                      </p>
                      {item.notes && <p className="text-xs text-muted-foreground mt-1">{item.notes}</p>}
                      {item.status === 'risolto' && (
                        <div className="mt-1 p-2 bg-blue-50 rounded text-xs">
                          <p className="text-blue-800"><strong>Risolto:</strong> {item.resolution_notes}</p>
                          <p className="text-blue-600">Da: {item.resolved_by} - {new Date(item.resolved_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}</p>
                        </div>
                      )}
                    </div>
                    {item.status === 'non_conforme' && (
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleResolveClick(index); }}>
                        <RotateCcw className="w-3 h-3 mr-1" /> Risolvi
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              {check.general_notes && (
                <div className="mt-4 p-3 bg-secondary/50 rounded-sm">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Note Generali</p>
                  <p className="text-sm">{check.general_notes}</p>
                </div>
              )}
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-border">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    const email = window.prompt('Inserisci email destinatario (UL o Azienda):');
                    if (email) {
                      safetyChecksApi.sendReport(check.id, email)
                        .then(() => toast.success(`Report inviato a ${email}`))
                        .catch(() => toast.error('Errore invio email. Verifica la configurazione.'));
                    }
                  }}
                >
                  <Mail className="w-4 h-4 mr-2" /> Invia via Email
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrintReport}>
                  <Printer className="w-4 h-4 mr-2" /> Stampa Report
                </Button>
              </div>
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Risolvi Non Conformità</DialogTitle>
          </DialogHeader>
          <ResolveDialog
            check={check}
            itemIndex={selectedItemIndex}
            inspectors={inspectors}
            onResolve={onRefresh}
            onClose={() => setResolveDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default function SafetyChecksPage() {
  const { user } = useAuth();
  const isReadOnly = user?.role === 'gerente' || user?.role === 'collaborator';
  const [selectedPdv, setSelectedPdv] = useState(null);
  const [checks, setChecks] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [pdvTemplates, setPdvTemplates] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [expandedCheck, setExpandedCheck] = useState(null);

  const fetchData = async () => {
    try {
      const [checksRes, templatesRes, pdvsRes, inspectorsRes] = await Promise.all([
        safetyChecksApi.getAll(selectedPdv ? { pdv_id: selectedPdv.id } : {}),
        checklistApi.getTemplates(),
        pdvApi.getAll(),
        inspectorsApi.getAll()
      ]);
      setChecks(checksRes.data);
      setAllTemplates(templatesRes.data);
      setPdvs(pdvsRes.data);
      setInspectors(inspectorsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const fetchPdvTemplates = async () => {
    if (selectedPdv && allTemplates.length > 0) {
      try {
        const response = await pdvChecklistsApi.getForPdv(selectedPdv.id);
        // Handle both response formats
        if (Array.isArray(response.data)) {
          setPdvTemplates(response.data);
        } else {
          const activeIds = response.data?.active_template_ids || [];
          const combinedTemplates = allTemplates.map(t => ({
            ...t,
            is_active_for_pdv: activeIds.includes(t.id)
          }));
          setPdvTemplates(combinedTemplates);
        }
      } catch (error) {
        console.error('Error fetching PDV templates');
        setPdvTemplates(allTemplates.map(t => ({ ...t, is_active_for_pdv: false })));
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedPdv]);

  useEffect(() => {
    if (allTemplates.length > 0) {
      fetchPdvTemplates();
    }
  }, [selectedPdv, allTemplates]);

  const handleSubmit = async (data) => {
    try {
      await safetyChecksApi.create(data);
      toast.success('Controllo completato con successo!');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const filteredChecks = checks.filter((check) => {
    const pdv = pdvs.find(p => p.id === check.pdv_id);
    const matchesSearch =
      check.template_name.toLowerCase().includes(search.toLowerCase()) ||
      check.inspector_name.toLowerCase().includes(search.toLowerCase()) ||
      (pdv?.name || '').toLowerCase().includes(search.toLowerCase());
    return matchesSearch;
  });

  const avgScore = checks.length > 0
    ? Math.round(checks.reduce((acc, c) => acc + c.conformity_score, 0) / checks.length)
    : 0;

  const actionRequired = checks.filter(c => c.status === 'requires_action').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div data-testid="safety-checks-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="page-header mb-0">
          <h1 className="page-title">Controlli Sicurezza</h1>
          <p className="page-subtitle">{checks.length} controlli {selectedPdv ? `per ${selectedPdv.name}` : 'totali'}</p>
        </div>
        <div className="flex gap-2">
          {selectedPdv && !isReadOnly && (
            <Button variant="outline" onClick={() => setConfigDialogOpen(true)}>
              <Settings className="w-4 h-4 mr-2" /> Configura Checklist
            </Button>
          )}
          {!isReadOnly && (
            <Button
              className="border-accent bg-accent hover:bg-accent/90"
              onClick={() => setDialogOpen(true)}
              disabled={!selectedPdv}
              data-testid="new-check-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Nuovo Controllo
            </Button>
          )}
        </div>
      </div>

      {/* PDV Selector */}
      <PDVSelector pdvs={pdvs} selectedPdv={selectedPdv} onSelect={setSelectedPdv} />

      {selectedPdv && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="hover-card">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
                  <ClipboardCheck className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-heading">{checks.length}</p>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Controlli</p>
                </div>
              </CardContent>
            </Card>
            <Card className="hover-card">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-emerald-100 flex items-center justify-center">
                  <Award className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className={`text-2xl font-bold font-heading ${avgScore >= 80 ? 'text-emerald-600' : avgScore >= 50 ? 'text-amber-600' : 'text-rose-600'}`}>
                    {avgScore}%
                  </p>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Media</p>
                </div>
              </CardContent>
            </Card>
            <Card className="hover-card">
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${actionRequired > 0 ? 'bg-rose-100' : 'bg-emerald-100'}`}>
                  <XCircle className={`w-6 h-6 ${actionRequired > 0 ? 'text-rose-600' : 'text-emerald-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold font-heading ${actionRequired > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                    {actionRequired}
                  </p>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Da Risolvere</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Cerca per checklist o ispettore..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Checks List */}
          {filteredChecks.length > 0 ? (
            <div className="space-y-4">
              {filteredChecks.map((check) => (
                <CheckCard
                  key={check.id}
                  check={check}
                  pdvs={pdvs}
                  inspectors={inspectors}
                  expanded={expandedCheck === check.id}
                  onToggle={() => setExpandedCheck(expandedCheck === check.id ? null : check.id)}
                  onRefresh={fetchData}
                />
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <ClipboardCheck className="empty-state-icon" />
              <p className="empty-state-title">Nessun controllo</p>
              <p className="empty-state-description">Effettua il primo controllo per questa Unità Locale</p>
              <Button className="mt-4 bg-accent hover:bg-accent/90" onClick={() => setDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" /> Nuovo Controllo
              </Button>
            </div>
          )}
        </>
      )}

      {!selectedPdv && (
        <div className="empty-state py-16">
          <Building className="empty-state-icon" />
          <p className="empty-state-title">Seleziona un'Unità Locale</p>
          <p className="empty-state-description">Scegli una UL dal menu sopra per visualizzare e gestire i controlli</p>
        </div>
      )}

      {/* New Check Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Nuovo Controllo Sicurezza</DialogTitle>
          </DialogHeader>
          <ChecklistForm
            pdv={selectedPdv}
            templates={pdvTemplates}
            inspectors={inspectors}
            onSubmit={handleSubmit}
            onCancel={() => setDialogOpen(false)}
            onInspectorAdded={() => inspectorsApi.getAll().then(res => setInspectors(res.data))}
          />
        </DialogContent>
      </Dialog>

      {/* Config Checklist Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Settings className="w-5 h-5" /> Configura Checklist
            </DialogTitle>
          </DialogHeader>
          {selectedPdv && (
            <ChecklistConfigDialog
              pdv={selectedPdv}
              templates={allTemplates}
              onUpdate={fetchPdvTemplates}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
